/**
 * Urban Nest Quest — Houston Neighborhood Configuration
 *
 * Single source of truth for every /apartments/<slug> landing page.
 * Each entry powers:
 *   - <title>, <meta description>, <h1>, canonical URL
 *   - Hero copy + intro block
 *   - Lead-source label for funnel attribution
 *   - JSON-LD LocalBusiness + Breadcrumb + FAQPage
 *   - "Related Neighborhoods" internal-linking grid
 */

export const NEIGHBORHOODS = {
  "energy-corridor": {
    slug: "energy-corridor",
    name: "Energy Corridor",
    leadSource: "energy_corridor",
    seoTitle: "Energy Corridor Apartments | Houston Apartment Locator | Urban Nest Quest",
    metaDescription:
      "Browse Energy Corridor apartments near BP, ConocoPhillips, and Citgo. Free apartment locating with Urban Nest Quest — luxury high-rises, family-friendly communities, and commute-friendly addresses.",
    h1: "Energy Corridor Apartments",
    eyebrow: "West Houston · Energy Corridor",
    intro:
      "Home to BP, ConocoPhillips, Citgo, and dozens of energy giants, the Energy Corridor is one of Houston's strongest rental submarkets. Expect modern mid-rise communities, gated developments, and quick access to I-10 — plus a surprising amount of green space along the Terry Hershey trail.",
    landmarks: ["BP Helios Plaza", "Terry Hershey Park", "Memorial Hermann Katy Hospital", "CityCentre", "I-10 / Beltway 8"],
    keywords: ["Energy Corridor Apartments", "Apartments Near BP Houston", "West Houston Apartments", "I-10 Apartments Houston"],
    geo: { lat: 29.7800, lng: -95.6300 },
    faqs: [
      { q: "What's the average rent in the Energy Corridor?",
        a: "Most one-bedroom apartments range from $1,400–$2,200 per month. Newer luxury communities and townhomes can run $2,500–$4,500+." },
      { q: "Is the Energy Corridor good for families?",
        a: "Yes — Katy ISD and SBISD zoning, multiple parks, and gated family-friendly communities make it popular with relocating professionals." },
      { q: "How long is the commute to Downtown Houston?",
        a: "Roughly 25–35 minutes via I-10 East depending on time of day. The METRO Park & Ride at Addicks offers a no-stress commute." },
    ],
  },

  "heights": {
    slug: "heights",
    name: "The Heights",
    leadSource: "heights",
    seoTitle: "The Heights Apartments Houston | Walkable Historic District | Urban Nest Quest",
    metaDescription:
      "Find apartments in The Heights — Houston's most walkable historic neighborhood. Boutique buildings, bungalow conversions, and modern townhomes near 19th Street shops and Heights Hike & Bike Trail.",
    h1: "The Heights Apartments",
    eyebrow: "Inner Loop · The Heights",
    intro:
      "Bungalow-lined streets, indie boutiques on 19th Street, and the best beer-and-taco scene in the city — The Heights is the rare Houston neighborhood you can actually walk. Stock ranges from converted bungalows to new mid-rise communities along Yale Street.",
    landmarks: ["Heights Hike & Bike Trail", "19th Street District", "M-K-T Heights", "White Oak Music Hall", "Donovan Park"],
    keywords: ["The Heights Apartments", "Houston Heights Rentals", "Walkable Apartments Houston", "Heights Bungalow Rentals"],
    geo: { lat: 29.7900, lng: -95.4000 },
    faqs: [
      { q: "What rents should I expect in The Heights?",
        a: "Studios start around $1,200. Most one-bedrooms in newer mid-rises are $1,700–$2,400. Townhomes and renovated bungalows can reach $3,500+." },
      { q: "Is The Heights pet-friendly?",
        a: "Very — most modern Heights buildings allow dogs, and the neighborhood has multiple dog parks plus the M-K-T trail." },
      { q: "Is parking an issue?",
        a: "Most communities offer covered or assigned parking. Street parking varies; ask about reserved spots before signing." },
    ],
  },

  "memorial": {
    slug: "memorial",
    name: "Memorial",
    leadSource: "memorial",
    seoTitle: "Memorial Apartments Houston | Luxury & Family Rentals | Urban Nest Quest",
    metaDescription:
      "Memorial apartments for rent in Houston — luxury high-rises near Memorial City Mall, family communities in Spring Branch ISD, and quiet wooded properties along Memorial Drive.",
    h1: "Memorial Apartments",
    eyebrow: "West Houston · Memorial",
    intro:
      "Tree-lined Memorial Drive, top-rated SBISD schools, and proximity to both Downtown and the Energy Corridor make Memorial one of Houston's most consistently desirable rental areas. Expect a mix of upscale high-rises, gated low-density communities, and pocket townhome neighborhoods.",
    landmarks: ["Memorial City Mall", "Town & Country Village", "Memorial Park", "Spring Branch ISD", "I-10 / Beltway 8"],
    keywords: ["Memorial Apartments Houston", "Spring Branch Apartments", "Memorial City Apartments", "Luxury Apartments West Houston"],
    geo: { lat: 29.7700, lng: -95.5500 },
    faqs: [
      { q: "Why do families prefer Memorial?",
        a: "SBISD's school ratings, low-density communities, and proximity to Memorial Park. Many transferees from out of state land here." },
      { q: "Are there luxury high-rises in Memorial?",
        a: "Yes — the Town & Country and Memorial City areas have several boutique luxury high-rises with concierge, gym, and pool amenities." },
    ],
  },

  "galleria": {
    slug: "galleria",
    name: "Galleria / Uptown",
    leadSource: "galleria",
    seoTitle: "Galleria Apartments Houston | Uptown High Rise Rentals | Urban Nest Quest",
    metaDescription:
      "Lease apartments in Houston's Galleria & Uptown district. Walk to The Galleria mall, fine dining, and Post Oak Boulevard. Luxury high-rises, concierge service, and free locator help.",
    h1: "Galleria & Uptown Apartments",
    eyebrow: "Uptown · Galleria",
    intro:
      "If walkable luxury is the brief, Uptown is the answer. Glass towers along Post Oak Boulevard, a five-minute walk to The Galleria, and some of Houston's best dining concentrated within a square mile. Most communities here are high-rise with full amenity decks.",
    landmarks: ["The Galleria", "Post Oak Boulevard", "Williams Tower & Water Wall", "Highland Village", "Uptown Park"],
    keywords: ["Galleria Apartments Houston", "Uptown Houston Rentals", "Post Oak Apartments", "High Rise Apartments Houston"],
    geo: { lat: 29.7400, lng: -95.4600 },
    faqs: [
      { q: "Are the Galleria apartments mostly luxury?",
        a: "Yes — the area skews high-rise and luxury. Expect rents from $2,000 studios to $10,000+ penthouses, with concierge and rooftop amenities at most buildings." },
      { q: "Is Uptown walkable?",
        a: "More than most of Houston. Post Oak's new boulevard design makes shops, restaurants, and parks accessible on foot from most buildings." },
    ],
  },

  "downtown-houston": {
    slug: "downtown-houston",
    name: "Downtown Houston",
    leadSource: "downtown_houston",
    seoTitle: "Downtown Houston Apartments | High Rise Rentals & Lofts | Urban Nest Quest",
    metaDescription:
      "Live where Houston works — Downtown apartments, high-rise lofts, and skyline-view rentals. Walk to Discovery Green, the theater district, and the Tunnel system.",
    h1: "Downtown Houston Apartments",
    eyebrow: "Central Houston · Downtown",
    intro:
      "Skyline-view high-rises, repurposed historic lofts, and direct access to the 7-mile underground Tunnel system — Downtown is for renters who want the urban core. Discovery Green concerts, Astros and Rockets games, and the theater district are all on foot.",
    landmarks: ["Discovery Green", "Minute Maid Park", "Toyota Center", "Theater District", "Houston Tunnel System"],
    keywords: ["Downtown Houston Apartments", "Houston High Rise Apartments", "Houston Lofts", "Apartments Near Discovery Green"],
    geo: { lat: 29.7600, lng: -95.3700 },
    faqs: [
      { q: "Is Downtown Houston safe for renters?",
        a: "The residential blocks (around Market Square, Discovery Green, and the south end) are well-lit, walkable, and have strong building security. Like any urban core, awareness matters." },
      { q: "Are there grocery options Downtown?",
        a: "Yes — Phoenicia Specialty Foods, Whole Foods at Post HTX, and a number of bodegas. Most high-rises have rideshare-friendly drop-off built in." },
    ],
  },

  "midtown": {
    slug: "midtown",
    name: "Midtown",
    leadSource: "midtown",
    seoTitle: "Midtown Houston Apartments | Walkable Bar & Restaurant District | Urban Nest Quest",
    metaDescription:
      "Midtown Houston apartments for rent — Houston's nightlife and dining hub. Walk to Bagby Park, Sawyer Yards, and the METRORail. Young professional, walkable, light-rail access.",
    h1: "Midtown Houston Apartments",
    eyebrow: "Central Houston · Midtown",
    intro:
      "Midtown is where Houston's twenty- and thirty-somethings land — bars and restaurants on every corner, a direct METRORail line to the Medical Center and Downtown, and a steady pipeline of new mid-rise buildings. Less luxury polish than Uptown, more energy.",
    landmarks: ["Bagby Park", "Sawyer Yards", "METRORail Red Line", "Continental Club", "Midtown Park"],
    keywords: ["Midtown Houston Apartments", "Midtown Rentals Houston", "Apartments Near Medical Center Houston", "Walkable Houston Apartments"],
    geo: { lat: 29.7400, lng: -95.3800 },
    faqs: [
      { q: "Is Midtown good for medical center commuters?",
        a: "Excellent — the Red Line stops in Midtown make TMC a 10–15 minute train ride, so many residents skip the car entirely." },
      { q: "What's nightlife like?",
        a: "Dense and varied — from craft cocktail bars on Main Street to brewpubs around Bagby. Expect weekend energy, especially on Washington-adjacent streets." },
    ],
  },

  "river-oaks": {
    slug: "river-oaks",
    name: "River Oaks",
    leadSource: "river_oaks",
    seoTitle: "River Oaks Apartments Houston | Luxury Inner Loop Rentals | Urban Nest Quest",
    metaDescription:
      "River Oaks luxury apartments — Houston's most prestigious zip code. Boutique buildings, tree-canopied streets, and walking distance to River Oaks District shopping.",
    h1: "River Oaks Apartments",
    eyebrow: "Inner Loop · River Oaks",
    intro:
      "Houston's most coveted address, full stop. River Oaks is known for tree-canopied streets, low-density boutique buildings, and proximity to both the Galleria and Downtown. Inventory here is intentionally limited — when something opens up, it goes fast.",
    landmarks: ["River Oaks District", "River Oaks Country Club", "Highland Village", "Buffalo Bayou Park", "St. Thomas High School"],
    keywords: ["River Oaks Apartments", "Luxury Apartments River Oaks", "Inner Loop Houston Luxury", "Houston Boutique Apartments"],
    geo: { lat: 29.7500, lng: -95.4200 },
    faqs: [
      { q: "Are there mid-rise options in River Oaks?",
        a: "Yes — boutique mid-rises along West Gray and Westheimer offer the address without the high-rise scale. Expect concierge service and reserved parking." },
      { q: "What's the typical rent in River Oaks?",
        a: "Studios start around $2,200. Most one-bedrooms run $3,000–$5,000, and luxury two-bedrooms can exceed $8,000." },
    ],
  },

  "west-university": {
    slug: "west-university",
    name: "West University",
    leadSource: "west_university",
    seoTitle: "West University Apartments Houston | Family-Friendly Inner Loop | Urban Nest Quest",
    metaDescription:
      "West University apartments — Houston's top-rated school district inside the Loop. Quiet residential streets, close to Rice University, Texas Medical Center, and Greenway Plaza.",
    h1: "West University Apartments",
    eyebrow: "Inner Loop · West University",
    intro:
      "If you want top public schools and Inner Loop access, West U is the sweet spot. The neighborhood is famously residential with limited apartment stock, but well-priced boutique buildings exist along Bissonnet, Buffalo Speedway, and University Boulevard.",
    landmarks: ["Rice University", "Texas Medical Center", "Greenway Plaza", "Rice Village", "West U Recreation Center"],
    keywords: ["West University Apartments", "West U Houston Rentals", "Apartments Near Rice University", "Apartments Near TMC Houston"],
    geo: { lat: 29.7200, lng: -95.4300 },
    faqs: [
      { q: "Are these apartments zoned for West U public schools?",
        a: "Buildings inside the West University city limits are; others may be HISD-zoned. Always verify school zoning at the specific address before signing." },
      { q: "Is West U a good fit for medical residents?",
        a: "Very — it's a short drive or bike ride to TMC, and the streets are calmer than Midtown or Med Center proper." },
    ],
  },

  "katy": {
    slug: "katy",
    name: "Katy",
    leadSource: "katy",
    seoTitle: "Katy Apartments | Katy ISD Family Rentals | Urban Nest Quest",
    metaDescription:
      "Katy, Texas apartments and townhomes for rent. Top-rated Katy ISD, master-planned communities, and easy I-10 access. Free locator service from Urban Nest Quest.",
    h1: "Katy Apartments & Rentals",
    eyebrow: "West Suburbs · Katy",
    intro:
      "Katy is the gold standard for Houston families chasing school zoning. Katy ISD consistently ranks among Texas's top districts, and Cinco Ranch, Cross Creek, and LaCenterra anchor the master-planned community offerings. Apartments here are newer, larger, and amenity-loaded.",
    landmarks: ["Katy ISD", "LaCenterra at Cinco Ranch", "Cross Creek Ranch", "Typhoon Texas", "Katy Mills Mall"],
    keywords: ["Katy Apartments", "Cinco Ranch Apartments", "Katy ISD Rentals", "Apartments West Houston Katy"],
    geo: { lat: 29.7800, lng: -95.8200 },
    faqs: [
      { q: "Why is Katy so popular for families?",
        a: "Katy ISD is the primary draw — many transferees prioritize zoning here. Master-planned community amenities, low crime, and newer housing stock seal the deal." },
      { q: "How long is the commute from Katy to Downtown?",
        a: "45–60 minutes by car at peak. The Park & Ride express bus from Katy to Downtown takes 35–50 minutes and runs HOV lanes." },
    ],
  },

  "cypress": {
    slug: "cypress",
    name: "Cypress",
    leadSource: "cypress",
    seoTitle: "Cypress TX Apartments | Bridgeland & Towne Lake Rentals | Urban Nest Quest",
    metaDescription:
      "Cypress, Texas apartments and townhome rentals near Bridgeland, Towne Lake, and Cy-Fair ISD. Family-friendly master-planned communities northwest of Houston.",
    h1: "Cypress Apartments & Rentals",
    eyebrow: "Northwest Suburbs · Cypress",
    intro:
      "Quieter than Katy, with the same family-first appeal. Cypress is anchored by Bridgeland and Towne Lake, two of Houston's most successful master-planned communities. Cy-Fair ISD schools are strong, and 290/99 give commuters multiple routes to Downtown, the Energy Corridor, and Galleria.",
    landmarks: ["Bridgeland", "Towne Lake", "Cy-Fair ISD", "Houston Premium Outlets", "Cypresswood Golf Club"],
    keywords: ["Cypress TX Apartments", "Bridgeland Apartments", "Towne Lake Rentals", "Cy-Fair ISD Apartments"],
    geo: { lat: 29.9700, lng: -95.7000 },
    faqs: [
      { q: "Is Cypress a good alternative to Katy?",
        a: "Yes — comparable school quality (Cy-Fair vs. Katy ISD), often slightly lower rents, and newer inventory. Commute direction is the main differentiator." },
      { q: "Are there rental homes available in Cypress?",
        a: "Yes — single-family rentals and townhomes are common in Bridgeland, Towne Lake, and adjacent subdivisions." },
    ],
  },

  "sugar-land": {
    slug: "sugar-land",
    name: "Sugar Land",
    leadSource: "sugar_land",
    seoTitle: "Sugar Land Apartments | Fort Bend ISD Rentals | Urban Nest Quest",
    metaDescription:
      "Sugar Land apartments and rental homes — Fort Bend ISD, Sugar Land Town Square, and Imperial. Master-planned living with easy access to Downtown Houston and the Medical Center.",
    h1: "Sugar Land Apartments & Rentals",
    eyebrow: "Southwest Suburbs · Sugar Land",
    intro:
      "Sugar Land combines top-rated Fort Bend ISD schools with one of the most walkable suburban downtowns in Texas — Sugar Land Town Square. Imperial, Riverstone, and Telfair anchor the master-planned communities. US-59/I-69 keeps commutes manageable.",
    landmarks: ["Sugar Land Town Square", "Smart Financial Centre", "Imperial Sugar Land", "Fort Bend ISD", "Constellation Field"],
    keywords: ["Sugar Land Apartments", "Fort Bend ISD Rentals", "Sugar Land Town Square Apartments", "Imperial Sugar Land Rentals"],
    geo: { lat: 29.5990, lng: -95.6150 },
    faqs: [
      { q: "What makes Sugar Land different from Katy?",
        a: "Sugar Land has a more developed walkable downtown (Town Square), tends to skew slightly more diverse, and has direct US-59 commutes to Downtown and TMC." },
      { q: "Are there luxury apartments in Sugar Land?",
        a: "Yes — Town Square, Riverstone, and the Imperial District all have luxury mid-rise communities with concierge and resort-style amenities." },
    ],
  },

  "woodlands": {
    slug: "woodlands",
    name: "The Woodlands",
    leadSource: "woodlands",
    seoTitle: "The Woodlands Apartments | Master-Planned Forest Living | Urban Nest Quest",
    metaDescription:
      "The Woodlands, Texas apartments for rent — master-planned community north of Houston with Market Street shops, Waterway, and Conroe ISD schools. Free locator service.",
    h1: "The Woodlands Apartments",
    eyebrow: "North Suburbs · The Woodlands",
    intro:
      "Forty miles of forested trails, Market Street's walkable shopping district, and the Cynthia Woods Mitchell Pavilion concert series — The Woodlands is Houston's masterclass in suburban planning. Apartment communities are integrated into the forest canopy, with most properties offering resort amenities.",
    landmarks: ["Market Street", "The Woodlands Waterway", "Cynthia Woods Mitchell Pavilion", "George Mitchell Nature Preserve", "Conroe ISD"],
    keywords: ["The Woodlands Apartments", "Woodlands TX Rentals", "Market Street Apartments", "North Houston Suburbs Apartments"],
    geo: { lat: 30.1700, lng: -95.5100 },
    faqs: [
      { q: "How far is The Woodlands from Downtown Houston?",
        a: "About 30 miles north. I-45 and the Hardy Toll Road keep peak commutes around 50–70 minutes by car. Park & Ride buses are popular with Downtown commuters." },
      { q: "Are The Woodlands apartments expensive?",
        a: "Comparable to Katy and Sugar Land. Expect one-bedrooms at $1,400–$2,200 and luxury two-bedrooms at $2,500–$4,000+." },
    ],
  },
};

export const NEIGHBORHOOD_SLUGS = Object.keys(NEIGHBORHOODS);

/** Popular subset for homepage card grid. */
export const HOMEPAGE_NEIGHBORHOODS = [
  "energy-corridor", "heights", "galleria",
  "downtown-houston", "katy", "sugar-land",
];

/** Used for "Related Neighborhoods" → top 4 picks, excluding the current slug. */
export const relatedNeighborhoods = (currentSlug, n = 4) => {
  return NEIGHBORHOOD_SLUGS.filter((s) => s !== currentSlug).slice(0, n);
};
